/**
 * API client for the Centre Operations -> Parent Onboarding tracker.
 *
 * Backend: oh_operations Django app, alfred-suffixed endpoints under
 * /api/v1/oh_operations/parent_onboarding/. Every `apiEndpoints.X` reference
 * lives INSIDE a function body (TDZ-safety — mirrors checklist.js / centre.js).
 */
import { sentryLogger } from 'sentry'

import { apiEndpoints } from './common'

import { makeRequestUrl } from '../app/helpers'
import { get, patch, post, remove } from '../request'

// Onboarding rows for a centre (one per student on the tracker).
const fetchParentOnboardingBoard = async (centreUuid) => {
  try {
    const response = await get(
      makeRequestUrl(`${apiEndpoints.parentOnboarding}/alfred`, {
        centre: centreUuid
      })
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching parent onboarding board')
    throw error
  }
}

// Add a student to the tracker (idempotent — returns the existing row if any).
const addStudentToOnboarding = async (studentUuid, centreUuid) => {
  try {
    const response = await post(`${apiEndpoints.parentOnboarding}/alfred`, {
      student: studentUuid,
      centre: centreUuid
    })
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in adding student to onboarding')
    throw error
  }
}

// Tick / untick one checklist step. `step` is an ONBOARDING_STEPS key.
const toggleOnboardingStep = async (uuid, step, checked) => {
  try {
    const response = await patch(
      `${apiEndpoints.parentOnboarding}/${uuid}/alfred`,
      {
        step,
        checked
      }
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in updating onboarding step')
    throw error
  }
}

const removeFromOnboarding = async (uuid) => {
  try {
    await remove(`${apiEndpoints.parentOnboarding}/${uuid}/alfred`)
    return uuid
  } catch (error) {
    sentryLogger(error, 'error in removing student from onboarding')
    throw error
  }
}

// Search students of a centre for the "Add student" picker. Reuses the existing
// student list endpoint (StudentFilter.centre_uuids -> enrolment->batch->centre).
const fetchStudentsForCentre = async (centreUuid, search) => {
  try {
    const response = await get(
      makeRequestUrl(apiEndpoints.student, {
        centre_uuids: centreUuid,
        search: search || undefined,
        limit: 50
      })
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching students for centre')
    throw error
  }
}

// --- Google Sheet bridge: staged onboarding-form submissions ---------------

// Pull new responses from the onboarding Google Sheet into staging.
const syncOnboardingSubmissions = async (tabFilter) => {
  try {
    const response = await post(
      `${apiEndpoints.parentOnboarding}/submissions/sync/alfred`,
      tabFilter ? { tab_filter: tabFilter } : {}
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in syncing onboarding submissions')
    throw error
  }
}

// Staged submissions for a centre (+ auto-suggested tracker match).
const fetchOnboardingSubmissions = async (centreUuid, statusValue = 'new') => {
  try {
    const response = await get(
      makeRequestUrl(`${apiEndpoints.parentOnboarding}/submissions/alfred`, {
        centre: centreUuid,
        status: statusValue
      })
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching onboarding submissions')
    throw error
  }
}

// Approve: link a submission to a tracker student and tick "form submitted".
const approveOnboardingSubmission = async (uuid, onboardingUuid) => {
  try {
    const response = await post(
      `${apiEndpoints.parentOnboarding}/submissions/${uuid}/approve/alfred`,
      { onboarding: onboardingUuid }
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in approving onboarding submission')
    throw error
  }
}

// Render the child's ID card (png|pdf) as a Blob. Bearer-gated stream, so it
// must be fetched (not linked) and shown via an object URL.
const fetchOnboardingCard = async (uuid, format = 'png') => {
  try {
    const response = await get(
      makeRequestUrl(
        `${apiEndpoints.parentOnboarding}/${uuid}/id_card/alfred`,
        {
          // `format` is a reserved query param on this API (any request carrying
          // it is rejected with 400 before reaching the view) — hence file_format.
          file_format: format
        }
      ),
      { responseType: 'blob' }
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching onboarding id card')
    // The response is a blob even on error — surface the server's detail so the
    // dialog can show the real reason instead of a generic message.
    try {
      const blob = error?.response?.data
      if (blob && typeof blob.text === 'function') {
        const txt = await blob.text()
        try {
          const parsed = JSON.parse(txt)
          error.serverDetail = parsed?.detail || txt.slice(0, 300)
          // 422 = card intentionally blocked; the reasons list is shown in
          // place of the card image.
          if (Array.isArray(parsed?.reasons) && parsed.reasons.length) {
            error.blockReasons = parsed.reasons
          }
        } catch (_) {
          error.serverDetail = txt.slice(0, 300)
        }
      }
    } catch (_) {
      /* ignore */
    }
    throw error
  }
}

// --- "Fix card details" — repair a blocked card without touching the sheet --

// Prefill for the fix dialog: merged child + adults with per-photo
// status/reason, who fixed what, and the current block reasons.
const fetchOnboardingCardFix = async (uuid) => {
  try {
    const response = await get(
      `${apiEndpoints.parentOnboarding}/${uuid}/card_fix/alfred`
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching card fix details')
    throw error
  }
}

// Save fixes: text fields (adult1_name, adult1_phone, adult2_name,
// adult2_phone) + File objects (child_photo, adult1_photo, adult2_photo).
// Multipart — photos are validated server-side the moment they land.
const saveOnboardingCardFix = async (uuid, { fields = {}, files = {} }) => {
  try {
    const form = new FormData()
    Object.entries(fields).forEach(([key, value]) => {
      if (value != null && String(value).trim()) form.append(key, value)
    })
    Object.entries(files).forEach(([slot, file]) => {
      if (file) form.append(`${slot}_photo`, file, file.name)
    })
    // 'Content-Type': undefined on purpose (mirrors issues.js): request.js
    // only defaults the header when the key is ABSENT, and axios must set
    // multipart/form-data WITH the boundary itself.
    const response = await post(
      `${apiEndpoints.parentOnboarding}/${uuid}/card_fix/alfred`,
      form,
      { headers: { 'Content-Type': undefined } }
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in saving card fix')
    throw error
  }
}

// Drop every manual fix and go back to the parent's original form answers.
const resetOnboardingCardFix = async (uuid) => {
  try {
    const response = await remove(
      `${apiEndpoints.parentOnboarding}/${uuid}/card_fix/alfred`
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in resetting card fix')
    throw error
  }
}

// Deliver the card via email/whatsapp and tick "Child ID card shared".
const sendOnboardingCard = async (uuid, { channels, phone, email }) => {
  try {
    const response = await post(
      `${apiEndpoints.parentOnboarding}/${uuid}/send_card/alfred`,
      { channels, phone, email }
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in sending onboarding card')
    throw error
  }
}

// Queue an ID-card send. Without uuids: every child at the centre whose card
// hasn't been shared yet. With uuids: exactly those rows (explicit selection).
// Returns {queued: [names], no_contact: [names]} — the actual sends run
// server-side in the background.
const bulkSendOnboardingCards = async (centreUuid, channels, uuids = []) => {
  try {
    const response = await post(
      `${apiEndpoints.parentOnboarding}/bulk_send_cards/alfred`,
      { centre: centreUuid, channels, onboarding_uuids: uuids }
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in bulk sending onboarding cards')
    throw error
  }
}

// Pull active School Care enrolments onto the tracker now. Returns the
// extraction summary {admissions, created, existing, no_centre, errors}.
const syncOnboardingEnrolments = async () => {
  try {
    const response = await post(
      `${apiEndpoints.parentOnboarding}/sync_enrolments/alfred`,
      {}
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in syncing onboarding enrolments')
    throw error
  }
}

// Tick "Openhouse app downloaded" for parents we can now see have the app
// (member-app login or a registered member-app device). Same sweep the hourly
// beat runs; called from Refresh so the step updates on demand. Returns {ticked}.
const tickAppDownloads = async () => {
  try {
    const response = await post(
      `${apiEndpoints.parentOnboarding}/tick_app_downloads/alfred`,
      {}
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in ticking app downloads')
    throw error
  }
}

// Run the director check-in Slack nudge sweep now (all centres). Returns the
// sweep summary {centres, sent, skipped, children}.
const nudgeDirectorCheckins = async () => {
  try {
    const response = await post(
      `${apiEndpoints.parentOnboarding}/nudge_checkins/alfred`,
      {}
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in nudging director check-ins')
    throw error
  }
}

// Preview the welcome message (exact outgoing text + prefill phone).
const fetchWelcomePreview = async (uuid) => {
  try {
    const response = await get(
      `${apiEndpoints.parentOnboarding}/${uuid}/send_form/alfred`
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching welcome preview')
    throw error
  }
}

// Send the parent the welcome message over WhatsApp (email channel removed —
// Alfred's parent emails aren't reliable). Ticks "Onboarding form sent".
const sendOnboardingForm = async (uuid, { phone }) => {
  try {
    const response = await post(
      `${apiEndpoints.parentOnboarding}/${uuid}/send_form/alfred`,
      { phone }
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in sending welcome message')
    throw error
  }
}

const ignoreOnboardingSubmission = async (uuid) => {
  try {
    const response = await post(
      `${apiEndpoints.parentOnboarding}/submissions/${uuid}/ignore/alfred`,
      {}
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in ignoring onboarding submission')
    throw error
  }
}

// The links a centre hands to a parent (enrolment form + WhatsApp group), from
// the sheet's Links tab. Fetched WITH the board so the Copy button can write to
// the clipboard on the user's own click — clipboard.writeText() is refused if it
// happens after an await, which is what broke the event-creative "Copy prompt".
const fetchOnboardingCentreLinks = async (centreUuid) => {
  try {
    const response = await get(
      makeRequestUrl(`${apiEndpoints.parentOnboarding}/centre_links/alfred`, {
        centre: centreUuid
      })
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching centre onboarding links')
    throw error
  }
}

export {
  fetchOnboardingCentreLinks,
  fetchParentOnboardingBoard,
  addStudentToOnboarding,
  toggleOnboardingStep,
  removeFromOnboarding,
  fetchStudentsForCentre,
  syncOnboardingSubmissions,
  fetchOnboardingSubmissions,
  approveOnboardingSubmission,
  ignoreOnboardingSubmission,
  bulkSendOnboardingCards,
  fetchOnboardingCard,
  fetchOnboardingCardFix,
  saveOnboardingCardFix,
  resetOnboardingCardFix,
  fetchWelcomePreview,
  sendOnboardingCard,
  sendOnboardingForm,
  nudgeDirectorCheckins,
  syncOnboardingEnrolments,
  tickAppDownloads
}
